/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {SystemMessageType} from '../util/interfaces';
import {ModelResponse, ResponseData} from "@dvrd/dvr-controls";
import {createSystemMessage, deleteSystemMessage, getSystemMessages, updateSystemMessage} from "../util/requestSender";

interface Props extends BaseProps {
    message?: string;
    message_type?: SystemMessageType;
    priority?: number;
    active?: boolean;
}

interface Fields extends BaseFields {
    message: string;
    message_type: SystemMessageType;
    priority: number;
    active: boolean;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    message: '',
    message_type: SystemMessageType.INFO,
    priority: 0,
    active: true,
};

const fieldsDefinition: { [key in keyof Fields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    message: createFieldDef(ModelFieldType.STRING),
    priority: createFieldDef(ModelFieldType.NUMBER),
    active: createFieldDef(ModelFieldType.BOOLEAN),
    message_type: createFieldDef(ModelFieldType.STRING, SystemMessageType.INFO),
};

const ignoreProps: Array<keyof Props> = [];

export default class SystemMessage extends BaseModel<Props, Fields> {
    static getAll = (callback: ModelResponse<SystemMessage[]>) => {
        getSystemMessages({
            callback: (data: ResponseData) => {
                const messages: SystemMessage[] = data.success ? data.system_messages.map((message: any) => new SystemMessage(message)) : [];
                callback(messages, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get message(): string {
        return this.fields.message;
    }

    set message(message: string) {
        this.fields.message = message;
    }

    get messageType(): SystemMessageType {
        return this.fields.message_type;
    }

    set messageType(message_type: SystemMessageType) {
        this.fields.message_type = message_type;
    }

    get priority(): number {
        return this.fields.priority;
    }

    set priority(priority: number) {
        this.fields.priority = priority;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(active: boolean) {
        this.fields.active = active;
    }

    save = (callback: ModelResponse<SystemMessage>) => {
        createSystemMessage({
            data: {
                message: this.message, message_type: this.messageType, active: this.active, priority: this.priority,
            }, callback: this.responseHandler(callback),
        })
    };

    update = (callback: ModelResponse<SystemMessage>) => {
        updateSystemMessage({
            data: {
                message: this.message,
                message_type: this.messageType,
                message_id: this.id,
                active: this.active,
                priority: this.priority
            }, callback: this.responseHandler(callback),
        })
    };

    delete = (callback: ModelResponse<SystemMessage>) => {
        deleteSystemMessage({
            message_id: this.id, callback: this.responseHandler(callback),
        });
    };

    private responseHandler = (callback: ModelResponse<SystemMessage>) => (data: ResponseData) => {
        if (data.success && data.system_message) this.build(data.system_message);
        callback(this, data.success, data);
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            // TODO
        }
    };
}